import { useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/auth/useAuth";
import { COLLECTIONS } from "@/firestore/collections";
import { useFirestoreDoc, usePaginatedQuery } from "@/firestore/hooks";
import type { CardRefundDoc, CardRefundProofSubmissionDoc } from "@/firestore/cardRefunds";
import type { PaymentDoc, WalletDoc } from "@/firestore/types";
import { CardRefundDetailView } from "./card-refunds/CardRefundDetailView";
import { useCardRefundCommand } from "./card-refunds/useCardRefundCommand";

export function CardRefundDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const { user, claims } = useAuth();
  return <RefundCase key={`${user?.uid}:${claims?.backendId}:${id}`} id={id} />;
}

function RefundCase({ id }: { id: string }) {
  const command = useCardRefundCommand();
  const refund = useFirestoreDoc<CardRefundDoc>(COLLECTIONS.cardRefunds, id);
  const [acceptedVersion, setAcceptedVersion] = useState<string | null>(null);
  const [lookupPayment, setLookupPayment] = useState("");
  const payment = useFirestoreDoc<PaymentDoc>(
    COLLECTIONS.payments,
    /^[0-9a-fA-F-]{36}$/.test(lookupPayment) ? lookupPayment : undefined,
  );
  const wallet = useFirestoreDoc<WalletDoc>(
    COLLECTIONS.wallets,
    refund.data?.payoutInstructions?.wallet,
  );
  const proof = useFirestoreDoc<CardRefundProofSubmissionDoc>(
    COLLECTIONS.cardRefundProofSubmissions,
    refund.data?.submittedProof?.id,
  );
  const history = usePaginatedQuery<CardRefundProofSubmissionDoc>(
    COLLECTIONS.cardRefundProofSubmissions,
    { filters: [["cardRefund", "==", id]], orderBy: [["createdAt", "desc"]] },
    25,
    !!id,
  );
  const waitingForProjection =
    !!acceptedVersion &&
    (!refund.data || Date.parse(refund.data.updatedAt) < Date.parse(acceptedVersion));

  return (
    <CardRefundDetailView
      refund={refund.data}
      loading={refund.loading}
      error={refund.error}
      stale={waitingForProjection}
      operatorId={command.operatorId}
      canAct={command.canAct}
      pending={command.pending}
      wallet={wallet.data}
      walletLoading={wallet.loading}
      walletError={wallet.error}
      proofs={history.data}
      proofLoading={history.loading}
      proofError={history.error ?? proof.error}
      hasMoreProofs={history.hasMore}
      onLoadMoreProofs={history.loadMore}
      activeProof={proof.data?.id === refund.data?.submittedProof?.id ? proof.data : null}
      originalPayment={payment.data}
      paymentLoading={payment.loading}
      paymentError={payment.error}
      onPaymentLookup={setLookupPayment}
      onCommand={async (action, updatedAt) => {
        const result = await command.execute(id, updatedAt, action);
        setAcceptedVersion(result.updatedAt);
        return result;
      }}
      onRefresh={() => {
        refund.refresh();
        wallet.refresh();
        proof.refresh();
        history.refresh();
        payment.refresh();
      }}
    />
  );
}
